/**
 * One click on "Report": what is sent, and where. A signed-in person's report goes to the database routine that
 * stamps who they are; a guest's goes to this app's own intake, which keeps a few fixed facts and nothing typed.
 * Nothing here reads the address's query or fragment, storage, or a form.
 */
import { isReportable, type Failure } from './failures';
import { supabase } from './supabase';
import { writeRpc } from './write';

export type ReportSurface = 'toast' | 'state' | 'boundary' | 'inline';

let signedIn = false;
/** Called by the session provider every time it resolves who is here. */
export function setReporter(isSignedIn: boolean): void {
  signedIn = isSignedIn;
}
export function reporterSignedIn(): boolean {
  return signedIn;
}

/** A guest can report a page or a server that failed — never a refusal, and never while offline. */
const GUEST_REASONS: ReadonlySet<string> = new Set(['server', 'load', 'crash']);
export function canReport(f: Failure): boolean {
  return signedIn ? isReportable(f) : GUEST_REASONS.has(f.reason);
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
/** The page with record ids and private links removed: no query, no fragment, ids → `:id`, link keys → `:token`. */
export function cleanRoute(pathname: string): string {
  return pathname
    .split(/[?#]/)[0]!
    .replace(UUID, ':id')
    .replace(/\/t\/[^/]+/g, '/t/:token')
    .split('/')
    .map((seg) => (/^[A-Za-z0-9_-]{24,}$/.test(seg) ? ':token' : seg))
    .join('/')
    .slice(0, 200);
}

const idAfter = (pathname: string, segment: string): string | null =>
  new RegExp(`/${segment}/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:/|$)`, 'i').exec(pathname)?.[1] ?? null;

/** Exactly these sixteen facts. Who is reporting is never among them: the database stamps that itself. */
export function buildReport(surface: ReportSurface, f: Failure): Record<string, unknown> {
  const path = typeof location !== 'undefined' ? location.pathname : '';
  const build = typeof __BUILD_INFO__ !== 'undefined' ? __BUILD_INFO__ : null;
  const win = typeof window !== 'undefined' ? window : null;
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  return {
    surface,
    reason: f.reason,
    code: f.code ?? null,
    route: cleanRoute(path),
    message: f.reportAs ?? f.message,
    details: f.details ?? null,
    api_path: f.apiPath ?? null,
    api_status: f.apiStatus ?? null,
    trace_id: f.traceId ?? null,
    version: build?.version ?? null,
    build: build?.sha ?? null,
    event_id: idAfter(path, 'events'),
    hotel_id: idAfter(path, 'properties'),
    ua: nav?.userAgent ?? null,
    viewport: win && typeof win.innerWidth === 'number' ? `${win.innerWidth}x${win.innerHeight}` : null,
    online: nav && typeof nav.onLine === 'boolean' ? nav.onLine : null,
  };
}

/** What a guest's report keeps: where it happened and with what build — no sentence, no id, nothing typed. */
const GUEST_KEYS = ['surface', 'route', 'api_path', 'api_status', 'build', 'version', 'ua', 'viewport'] as const;

/** `capped`: the store answered, and kept nothing — this person has sent ten reports in the last ten minutes. */
export type ReportOutcome = 'sent' | 'capped' | 'failed';

export const REPORT_FAILED = "Couldn't send the report — please try again.";
/** The store answered and kept nothing: ten reports in ten minutes is the most one person may send. */
export const REPORT_CAPPED = "Couldn't send the report — please try again in a few minutes.";
/** What is said when a report was not taken. Neither sentence is ever itself offered for reporting. */
export const reportProblem = (outcome: Exclude<ReportOutcome, 'sent'>): string => (outcome === 'capped' ? REPORT_CAPPED : REPORT_FAILED);

/** True only when the report was taken. A failed report is never itself offered for reporting. */
export async function sendReport(surface: ReportSurface, f: Failure, overrides: { eventId?: string | null } = {}): Promise<boolean> {
  return (await submitReport(surface, f, overrides)) === 'sent';
}

export async function submitReport(surface: ReportSurface, f: Failure, overrides: { eventId?: string | null } = {}): Promise<ReportOutcome> {
  const p = buildReport(surface, f);
  if (overrides.eventId) p.event_id = overrides.eventId;
  if (signedIn) {
    // the routine answers with the stored report's number (the earlier one, for the same problem reported again within
    // ten minutes); no number means this person is past ten reports in ten minutes and nothing was kept
    const r = await writeRpc<number | null>(supabase, 'report_error', { p }, { noStash: true });
    return !r.ok ? 'failed' : typeof r.data === 'number' ? 'sent' : 'capped';
  }
  try {
    const body: Record<string, unknown> = {};
    for (const k of GUEST_KEYS) if (p[k] !== null) body[k] = p[k];
    const r = await fetch('/api/report', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return r.status === 204 ? 'sent' : 'failed';
  } catch {
    return 'failed';
  }
}
